import React, {useState, useEffect, useRef} from 'react'
import {cloneDeep} from 'lodash'
import {select} from 'd3'

import {ValueBox} from '../utils/ValueWrappers'
import DrillBit from '../utils/DrillBit'
import {
    y2WellSections,
    y2WellSectionsWithBegin,
    wellSectionWidth,
    pointsCasing,
    positionCasingShoe,
    sectionValueBoxConnectorPoints,
    bitDepthvalueBoxConnectorPoints,
    holeDepthValueBoxConnectorPoints,
    positionX,
    positionXForBoxes, safepositionXForBoxes,
    positionXForLinesRange, positionXForLines
} from '../utils/utils'
import {CasingShoeBullet, CasingShoe} from '../utils/CasingShoe'

interface HoleCleaningTypes {
    uom?: string
    bitDepth?: number
    holeDepth?: number
    theme: string
    drillBitPosition: number
    sections: Array<OutputSectionTypes>
    prevBitDepth?: number
    range: Array<number>
    originalRange: Array<number>
    showDrillBit: boolean
    bottomComponent: SchematicValues['bottomComponent']
    isZoomed: boolean
    hideHoleDepth: boolean
    width: number
    height: number
    lithology: boolean
    translated: number
}

const positioningDrillBit = (range, proportion, bitDepth, discountRange0, holeDepth): number =>
    range.length > 0 ? proportion * bitDepth - 29 - discountRange0 : bitDepth >= holeDepth ? 582 : 538

const transitioningElement = (el, attr, value, dur): void => {
    if(document.hidden) select(el).attr(attr, value)
    else select(el).transition().duration(dur).attr(attr, value)
} 
const initalRangeX = (width, value, translated, size, numberOfSections, lithology): number => {
    // const x = lithology ? 62 : (numberOfSections > 3 ? 86 : 79)
    const x = width - 15.5 - Math.floor(value > 0 ? Math.log10(value) : 0)*6 - (lithology ? 27 : 0) - 1.5*translated
    return x
}
export default function HoleCleaningSchematic({
                          uom,
                          bitDepth,
                          holeDepth,
                          theme,
                          sections,
                          prevBitDepth,
                          range,
                          showDrillBit,
                          bottomComponent,
                          isZoomed,
                          originalRange,
                          hideHoleDepth,
                          ...props
                      }: HoleCleaningTypes): JSX.Element {
    const [sectionsSortedBox, setSectionsSortedBox] = useState([])
    const [sectionsSorted, setSectionsSorted] = useState([])
    const [numberOfSectionsBox, setNumberOfSectionsBox] = useState(0)
    const [numberOfSections, setNumberOfSections] = useState(0)
    const [proportion, setProportion] = useState(range.length > 0 ? props.height / (range[1] - range[0]) : 0)
    const [discountRange0, setDiscountRange0] = useState(range.length > 0 ? proportion * range[0] : 0)
    const [drillPosition, setDrillPosition] = useState(
        positioningDrillBit(range, proportion, bitDepth, discountRange0, holeDepth)
    )
    const [prevDrillPosition, setPrevDrillPosition] = useState(0)
    const [xPosition, setXPosition] = useState(positionX(numberOfSections))
    const [size, setSize] = useState<'sm' | 'md' | 'lg'>('lg')

    const [uomState, setUomState] = useState(uom ?? '')
    const drillRef = useRef(null)
    const drillStringRef = useRef(null)
    const drillStringMarginRef = useRef(null)

    useEffect(() => setUomState(uom ?? ''), [uom])

    useEffect(() => setProportion(range.length > 0 ? props.height / (range[1] - range[0]) : 0), [
        JSON.stringify(range),
        props.height
    ])

    useEffect(() => setDiscountRange0(range.length > 0 ? proportion * range[0] : 0), [proportion, props.height])

    useEffect(() => {
        const zoomedDrillPosition = positioningDrillBit(range, proportion, bitDepth, discountRange0, holeDepth)
        if ((drillRef?.current, drillStringRef?.current)) {
            transitioningElement(drillRef?.current, 'y', zoomedDrillPosition, 100)
            transitioningElement(drillStringRef?.current, 'y2', zoomedDrillPosition, 100)
        }
        if ((drillRef?.current, drillStringMarginRef?.current)) {
            transitioningElement(drillRef?.current, 'y', zoomedDrillPosition, 100)
            transitioningElement(drillStringMarginRef?.current, 'y2', zoomedDrillPosition, 100)
        }
        setPrevDrillPosition(zoomedDrillPosition)
    }, [bitDepth, proportion, discountRange0, props.height])

    useEffect(
        () =>
            setDrillPosition(prevState => {
                setPrevDrillPosition(prevState)

                return positioningDrillBit(range, proportion, bitDepth, discountRange0, holeDepth)
            }),
        [bitDepth, props.height, proportion, discountRange0]
    )

    useEffect(() => {
        if ((drillRef?.current, drillStringRef?.current)) {
            const dur = prevDrillPosition === 0 ? 1 : 1000
            transitioningElement(drillRef?.current, 'y', drillPosition, dur)
            transitioningElement(drillStringRef?.current, 'y2', drillPosition, dur)
        }
    }, [drillPosition, drillRef?.current, drillStringRef?.current, props.height])

    useEffect(() => {
        if ((drillRef?.current, drillStringMarginRef?.current)) {
            const dur = prevDrillPosition === 0 ? 1 : 1000
            transitioningElement(drillRef?.current, 'y', drillPosition, dur)
            transitioningElement(drillStringMarginRef?.current, 'y2', drillPosition, dur)
        }
    }, [drillPosition, drillRef?.current, drillStringMarginRef?.current, props.height])

    useEffect(() => {
        const casingSections = sections.filter(d => d?.type !== 'bitDepth' && d?.type !== 'holeDepth')

        setSectionsSortedBox(sections)
        setSectionsSorted(casingSections)
        setNumberOfSectionsBox(sections.length)
        setNumberOfSections(casingSections.length)
    }, [JSON.stringify(sections)])

    useEffect(() => {
        setXPosition(positionX(numberOfSections))
    }, [numberOfSections])

    useEffect(() => setSize(props.width < 159 ? 'sm' : props.width < 210 ? 'md' : 'lg'), [props.width])

    return (
        <g className="svg-schematic">

            {range?.length > 0 && size !== 'sm' && (
                <>
                    <line x1="80" x2={props.lithology ? "95" : "100"} y1="1" y2="1" className="starting-range-line"/>
                    <text x={props.lithology ? "95" : "100"} y="11" className="starting-range">
                        {range[0] + ' ' + uomState}
                    </text>
                    <line
                        x1="80"
                        x2={props.lithology ? "95" : "100"}
                        y1={props.height - 1}
                        y2={props.height - 1}
                        className="ending-range-line"
                    />
                    <text x={props.lithology ? "95" : "100"} y={props.height} className="ending-range">
                        {isZoomed ? range[1] : originalRange[1] + ' ' + uomState}
                    </text>
                </>
            )}

            {sectionsSortedBox.map((e, i) => {
                if (e.type == 'bitDepth') {
                    // The bitDepth is being represented as a section to be sorted with them
                    if (showDrillBit)
                        return (
                            <React.Fragment key={i}>
                                <ValueBox
                                    points={bottomComponent && bitDepthvalueBoxConnectorPoints(
                                        numberOfSectionsBox,
                                        i,
                                        sectionsSortedBox[i],
                                        proportion,
                                        bitDepth,
                                        discountRange0,
                                        range,
                                        props.height, props.width, props.lithology, props.translated
                                    )}
                                    y={
                                        range.length > 0
                                            ? ((props.height * 0.8) / numberOfSectionsBox) * (i + 1) - 4
                                            : (569 / numberOfSectionsBox) * (i + 1) - 24
                                    }
                                    height={prevBitDepth ? '58' : '44'}
                                    h2={
                                        typeof bottomComponent === 'string' && bottomComponent?.toLowerCase()?.includes('casing')
                                            ? 'CASING DEPTH'
                                            : !bottomComponent
                                                ? 'CURRENT DEPTH'
                                                : 'BIT DEPTH'
                                    }
                                    directionDown={prevBitDepth <= bitDepth}
                                    p={(bitDepth ?? '-') + ' ' + uomState}
                                    p2={prevBitDepth ? (prevBitDepth ?? '-') + ' ' + uomState : ''}
                                    theme={theme}
                                    className={'line-bit-depth'}
                                    x={safepositionXForBoxes(numberOfSectionsBox, props.width, props.lithology, props.translated).toString()}
                                    size={size}
                                    numberOfSections={numberOfSectionsBox}
                                    range={range}
                                    lithology={props.lithology}
                                />
                            </React.Fragment>
                        )
                } else if (e.type == 'holeDepth') {
                    // The holeDepth is being represented as a section to be sorted with them
                    if (!hideHoleDepth)
                        return (
                            <ValueBox
                                // x,y x,y
                                points={sectionValueBoxConnectorPoints(
                                    numberOfSectionsBox,
                                    i,
                                    sectionsSortedBox[i],
                                    proportion,
                                    discountRange0,
                                    range,
                                    props.height, props.width, props.lithology, props.translated
                                )}
                                y={
                                    range.length > 0
                                        ? ((props.height * 0.8) / numberOfSectionsBox) * (i + 1) - 4
                                        : '555'
                                }
                                height="44"
                                h2="HOLE DEPTH"
                                p={(holeDepth ?? '-') + ' ' + uomState}
                                theme={theme}
                                x={safepositionXForBoxes(numberOfSectionsBox, props.width, props.lithology, props.translated).toString()}
                                size={size}
                                numberOfSections={numberOfSectionsBox}
                                range={range}
                                lithology={props.lithology}
                            />
                        )
                } else {
                    return (
                        <React.Fragment key={i}>
                            <ValueBox
                                points={sectionValueBoxConnectorPoints(
                                    numberOfSectionsBox,
                                    i,
                                    sectionsSortedBox[i],
                                    proportion,
                                    discountRange0,
                                    range,
                                    props.height, props.width, props.lithology, props.translated
                                )}
                                y={
                                    range.length > 0
                                        ? ((props.height * 0.8) / numberOfSectionsBox) * (i + 1) - 4
                                        : (569 / numberOfSectionsBox) * (i + 1) - 24
                                }
                                height="44"
                                h2={e.name ?? 'SECTION ' + e.bit_diameter}
                                p={(e.md_casing_shoe ?? '-') + ' ' + uomState}
                                theme={theme}
                                className="line-bit-depth"
                                x={safepositionXForBoxes(numberOfSectionsBox, props.width, props.lithology, props.translated).toString()}
                                size={size}
                                numberOfSections={numberOfSectionsBox}
                                range={range}
                                lithology={props.lithology}
                            />
                        </React.Fragment>
                    )
                }
            })}

            {sectionsSorted.map((e, i) => {
                const isOpenHole = Boolean(e.open_hole)

                return (
                    <>
                        <line
                            className={`well-sections ${isOpenHole ? 'well-sections--open-hole' : ''}`}
                            x1={xPosition}
                            x2={xPosition}
                            y1="0"
                            y2={y2WellSections(
                                range,
                                proportion,
                                sectionsSorted[i].md_casing_shoe,
                                discountRange0,
                                numberOfSections,
                                i
                            )}
                            strokeWidth={wellSectionWidth(numberOfSections, i, false)}
                        />

                        {sectionsSorted[i].casing_begin && range.length > 0 && (
                            <line
                                className={`well-sections before-begin ${isOpenHole ? 'well-sections--open-hole' : ''}`}
                                x1={xPosition}
                                x2={xPosition}
                                y1="0"
                                y2={y2WellSectionsWithBegin(
                                    range,
                                    proportion,
                                    sectionsSorted[i].casing_begin,
                                    discountRange0,
                                    numberOfSections,
                                    i
                                )}
                                strokeWidth={wellSectionWidth(numberOfSections, i, true)}
                            />
                        )}
                        {isOpenHole ? null : (
                            <>
                                <polyline
                                    points={pointsCasing(
                                        sectionsSorted[i],
                                        proportion,
                                        i,
                                        discountRange0,
                                        range,
                                        numberOfSections,
                                        'left'
                                    )}
                                    className="casing left"
                                />

                                <polygon
                                    points="0,8 8,0 8,8"
                                    className="casing-shoe left"
                                    style={{
                                        transform: positionCasingShoe(
                                            sectionsSorted[i].md_casing_shoe,
                                            proportion,
                                            range,
                                            discountRange0,
                                            numberOfSections,
                                            i,
                                            'left'
                                        )
                                    }}
                                />
                                <polyline
                                    points={pointsCasing(
                                        sectionsSorted[i],
                                        proportion,
                                        i,
                                        discountRange0,
                                        range,
                                        numberOfSections,
                                        'right'
                                    )}
                                    className="casing right"
                                />
                                <polygon
                                    points="8,8 0,0 0,8"
                                    className="casing-shoe right"
                                    style={{
                                        transform: positionCasingShoe(
                                            sectionsSorted[i].md_casing_shoe,
                                            proportion,
                                            range,
                                            discountRange0,
                                            numberOfSections,
                                            i,
                                            'right'
                                        )
                                    }}
                                />
                            </>
                        )}
                    </>
                )
            })}

            <line
                className="main-path"
                x1={xPosition}
                x2={xPosition}
                y1="0"
                y2={range.length > 0 ? proportion * holeDepth - discountRange0 : '613'}
            />

            {(bottomComponent !== 'drillBit') && (
                <line
                    ref={drillStringMarginRef}
                    className={'drill-string-margin'}
                    x1={xPosition}
                    x2={xPosition}
                    y1="0"
                    y2="468"
                />
            )}

            <line
                ref={drillStringRef}
                className={'drill-string ' + (bottomComponent === 'drillBit' ? '' : 'without-drill-bit')}
                x1={xPosition}
                x2={xPosition}
                y1="0"
                y2="468"
            />

            {bottomComponent === 'drillBit' && <DrillBit x={xPosition - 7.5} refDrill={drillRef}/>}
            {bottomComponent === 'casingShoe' && <CasingShoe x={xPosition - 7.5} ref={drillRef}/>}
            {bottomComponent === 'casingShoeBullet' && <CasingShoeBullet x={xPosition - 7.5} ref={drillRef}/>}

            {range?.length > 0 && size === 'sm' && (
                <>
                    <text x={initalRangeX(props.width, isZoomed ? range[0] : originalRange[0], props.translated, size, numberOfSections, props.lithology).toString()} y="11"
                          className="starting-range">
                        {isZoomed ? range[0] : originalRange[0] + ' ' + uomState}
                    </text>
                    <text x={(initalRangeX(props.width, isZoomed ? range[1] : originalRange[1], props.translated, size, numberOfSections, props.lithology)).toString()} y={props.height}
                          className="ending-range">
                        {isZoomed ? range[1] : originalRange[1] + ' ' + uomState}
                    </text>
                </>
            )}
        </g>
    )
}